import { ActionIcon, Alert, Button, Group, NumberInput, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { glave, obNapaki } from './zeton';

type Stopnja = Shema['BonusStopnja'];
type Vrstica = { odZneska: number | string; odstotek: number | string };

const KLJUC = ['skrbnik', 'bonusi'];

async function klic<T>(k: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  try {
    return await podatki(k);
  } catch (e) {
    obNapaki(e);
    throw e;
  }
}

/**
 * Skrbništvo → Bonusi: lestvica bonus točk za posamezno nakazilo (od zneska → odstotek; velja najvišja stopnja, ki jo
 * nakazilo doseže). Prazna lestvica = brez bonusov. Preverja in računa strežnik.
 */
export function Bonusi() {
  const bonusi = useQuery({
    queryKey: KLJUC,
    queryFn: ({ signal }) => klic(api.GET('/api/skrbnik/bonusi', { signal, headers: glave() })),
  });
  if (!bonusi.data) {
    return (
      <Kartica>
        <StanjeNalaganja
          napaka={bonusi.error}
          ponovi={() => void bonusi.refetch()}
          naslov="Lestvice bonusov ni bilo mogoče naložiti"
        />
      </Kartica>
    );
  }
  return <Urejanje key={JSON.stringify(bonusi.data)} stopnje={bonusi.data} />;
}

function Urejanje({ stopnje }: { stopnje: Stopnja[] }) {
  const odjemalec = useQueryClient();
  const zacetne = stopnje.map((s) => ({ odZneska: s.odZneska ?? '', odstotek: s.odstotek ?? '' }));
  const [vrstice, setVrstice] = useState<Vrstica[]>(zacetne);
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const spremenjeno = JSON.stringify(vrstice) !== JSON.stringify(zacetne);

  const nastavi = (i: number, v: Partial<Vrstica>) => setVrstice(vrstice.map((r, j) => (j === i ? { ...r, ...v } : r)));

  const shrani = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      const s = await klic(
        api.PUT('/api/skrbnik/bonusi', {
          headers: glave(),
          body: vrstice.map((r) => ({
            odZneska: r.odZneska === '' ? undefined : Number(r.odZneska),
            odstotek: r.odstotek === '' ? undefined : Number(r.odstotek),
          })),
        }),
      );
      odjemalec.setQueryData(KLJUC, s);
      notifications.show({ color: 'green', message: 'Lestvica bonusov je shranjena.' });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Kartica naslov="Lestvica bonusov">
      <Stack gap="sm" maw={560}>
        <Text size="sm" c="dimmed">
          Za posamezno nakazilo vsaj danega zneska dobi stranka poleg točk (1 € = 1 točka) še odstotek točk; velja
          najvišja stopnja, ki jo nakazilo doseže. Prazna lestvica = brez bonusov.
        </Text>
        {vrstice.map((r, i) => (
          <Group key={i} gap="xs" align="flex-start" wrap="nowrap">
            <NumberInput
              size="md"
              label={i === 0 ? 'Od zneska (€)' : undefined}
              aria-label={`Od zneska, stopnja ${i + 1}`}
              value={r.odZneska}
              onChange={(v) => nastavi(i, { odZneska: v })}
              min={0}
              decimalScale={2}
              decimalSeparator=","
              thousandSeparator="."
              hideControls
              error={napake[`odZneska.${i}`]}
              style={{ flex: 1 }}
            />
            <NumberInput
              size="md"
              label={i === 0 ? 'Bonus (%)' : undefined}
              aria-label={`Bonus, stopnja ${i + 1}`}
              value={r.odstotek}
              onChange={(v) => nastavi(i, { odstotek: v })}
              min={0}
              max={100}
              decimalScale={2}
              decimalSeparator=","
              hideControls
              error={napake[`odstotek.${i}`]}
              style={{ flex: 1 }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              size="lg"
              mt={i === 0 ? 25 : 0}
              aria-label={`Odstrani stopnjo ${i + 1}`}
              onClick={() => setVrstice(vrstice.filter((_, j) => j !== i))}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Group>
        ))}
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            onClick={() => setVrstice([...vrstice, { odZneska: '', odstotek: '' }])}
          >
            Dodaj stopnjo
          </Button>
          <Button onClick={() => void shrani()} loading={dela} disabled={!spremenjeno}>
            Shrani
          </Button>
        </Group>
      </Stack>
    </Kartica>
  );
}
